import { osnovaApi } from '../base';
import type { PtoExecutiveAct } from '../types';
import type {
  FieldAnomaly,
  FieldAssignableUser,
  FieldDailyReport,
  FieldElement,
  FieldInfo,
  FieldInspectionRequest,
  FieldMaterialReceipt,
  FieldPhoto,
  FieldRdPosition,
  FieldReportNote,
  FieldReportResource,
  FieldReportUsage,
  FieldReportWork,
  FieldSite,
  FieldTitul,
  FieldSiteMember,
  FieldTask,
  VolumeFact,
  VolumeFactEvent,
} from '@entities/field/types';
import type { FunctionRequest, RequestResponse, RequestTransition } from '@entities/requests/types';
import { parseResponse } from '../parseResponse';
import { parseFilenameFromDisposition } from '@shared/lib/downloadFile';

/** Файл ответа: `File` с именем из `Content-Disposition`; ошибку — как JSON,
 *  чтобы UI показал причину, а не «[object Blob]». */
async function fileResponse(response: Response): Promise<unknown> {
  if (!response.ok) return parseResponse(response);
  const blob = await response.blob();
  const name = parseFilenameFromDisposition(response.headers.get('content-disposition'));
  return name ? new File([blob], name, { type: blob.type }) : blob;
}

export const fieldApi = osnovaApi.injectEndpoints({
  endpoints: (b) => ({
    /** Карточка агента ОСНОВА.Совещания + перечень модулей витрины. */
    // ── ОСНОВА.Прораб (полевой сбор данных, веб-витрина обзора) ──
    getFieldInfo: b.query<FieldInfo, void>({
      query: () => '/agents/field/info',
      providesTags: ['Field'],
    }),
    /** Площадки прораба (обзор для офиса): глобальный доступ видит все площадки
     *  проекта при `project_id`, иначе — только свои по членству. */
    getFieldSites: b.query<{ sites: FieldSite[] }, { projectId?: string } | void>({
      query: (arg) => {
        const pid = arg ? arg.projectId : undefined;
        return pid ? `/agents/field/sites?project_id=${pid}` : '/agents/field/sites';
      },
      providesTags: ['Field'],
    }),
    /** Одна площадка (шапка детали). */
    getFieldSite: b.query<{ site: FieldSite }, string>({
      query: (id) => `/agents/field/sites/${id}`,
      providesTags: ['Field'],
    }),
    /** Подключить объект ПТО к Прорабу — завести площадку проекта (только
     *  проверяющий ПТО / администратор; прораб площадки не заводит). */
    createFieldSite: b.mutation<
      { site: FieldSite },
      { project_id: string; titul_id: string; name: string; code?: string; location?: string }
    >({
      query: (body) => ({ url: '/agents/field/sites', method: 'POST', body }),
      invalidatesTags: ['Field'],
    }),
    getFieldTituls: b.query<{ tituls: FieldTitul[] }, string>({
      query: (projectId) => `/agents/field/projects/${projectId}/tituls`,
      providesTags: ['Field'],
    }),
    createFieldTitul: b.mutation<
      { titul: FieldTitul },
      { projectId: string; code: string; name: string }
    >({
      query: ({ projectId, ...body }) => ({
        url: `/agents/field/projects/${projectId}/tituls`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Field'],
    }),
    updateFieldTitul: b.mutation<{ titul: FieldTitul }, { id: string; code: string; name: string }>(
      {
        query: ({ id, ...body }) => ({ url: `/agents/field/tituls/${id}`, method: 'PATCH', body }),
        invalidatesTags: ['Field'],
      },
    ),
    setFieldTitulDocuments: b.mutation<
      { titul: FieldTitul; document_ids: string[] },
      { id: string; document_ids: string[] }
    >({
      query: ({ id, ...body }) => ({
        url: `/agents/field/tituls/${id}/documents`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Field'],
    }),
    setFieldSiteTitul: b.mutation<{ site: FieldSite }, { id: string; titul_id: string | null }>({
      query: ({ id, ...body }) => ({ url: `/agents/field/sites/${id}`, method: 'PATCH', body }),
      invalidatesTags: ['Field'],
    }),
    /** Участники площадки (прорабы, начальник участка, рабочие). */
    getFieldSiteMembers: b.query<{ members: FieldSiteMember[] }, string>({
      query: (siteId) => `/agents/field/sites/${siteId}/members`,
      providesTags: ['Field'],
    }),
    /** Кого можно назначить на площадку (поиск по ФИО/почте). */
    getFieldAssignableUsers: b.query<
      { users: FieldAssignableUser[] },
      { siteId: string; q?: string }
    >({
      query: ({ siteId, q }) => ({
        url: `/agents/field/sites/${siteId}/assignable-users`,
        params: q ? { q } : undefined,
      }),
      providesTags: ['Field'],
    }),
    /** Назначить участника площадки (по умолчанию — прораб). */
    addFieldSiteMember: b.mutation<
      { member: FieldSiteMember },
      { siteId: string; user_id: string; role?: string }
    >({
      query: ({ siteId, user_id, role }) => ({
        url: `/agents/field/sites/${siteId}/members`,
        method: 'POST',
        body: { user_id, role: role ?? 'foreman' },
      }),
      invalidatesTags: ['Field'],
    }),
    /** Конструктивные элементы площадки. */
    getFieldElements: b.query<{ elements: FieldElement[] }, string>({
      query: (id) => `/agents/field/sites/${id}/elements`,
      providesTags: ['Field'],
    }),
    /** Конструктив из позиций объёма РД проекта (`position_ids` не задан — все
     *  позиции; уже заведённые сервер пропускает и считает в `skipped`). */
    createFieldElementsFromRd: b.mutation<
      {
        created: FieldElement[];
        skipped: number;
        offset: number;
        limit: number;
        processed: number;
        total: number;
        has_more: boolean;
      },
      { siteId: string; position_ids?: string[]; offset?: number; limit?: number }
    >({
      query: ({ siteId, position_ids, offset, limit }) => ({
        url: `/agents/field/sites/${siteId}/elements/from-rd`,
        method: 'POST',
        body: { position_ids, offset, limit },
      }),
      invalidatesTags: (result) => (result && !result.has_more ? ['Field'] : []),
    }),
    /** Позиции объёма РД действующих ревизий проекта площадки (выбор позиции
     *  при перепривязке элемента; только проверяющему ПТО). Без заголовков и
     *  отбракованных; у позиции — needs_review, decision, qualifier и
     *  missing_in_extraction (поля может не быть у старого сервера). */
    getFieldSiteRdPositions: b.query<{ positions: FieldRdPosition[] }, string>({
      query: (siteId) => `/agents/field/sites/${siteId}/rd-positions`,
      providesTags: ['Field'],
    }),
    /** Перепривязать элемент к позиции РД (после «Извлечь объёмы» привязка могла
     *  сняться, или выбрана не та): факты элемента без позиции (со строками ОЖР) и
     *  нерешённые с прежней позиции получают её же — `relinked_facts`. Принятые на
     *  прежней позиции остаются там (решения ПТО и строки ОЖР) — их число в
     *  `kept_accepted_facts` (поля нет у старого сервера). */
    setFieldElementPosition: b.mutation<
      { element: FieldElement; relinked_facts: number; kept_accepted_facts?: number },
      { elementId: string; position_id: string | null }
    >({
      query: ({ elementId, position_id }) => ({
        url: `/agents/field/elements/${elementId}/position`,
        method: 'POST',
        body: { position_id },
      }),
      invalidatesTags: ['Field', 'PtoOzhr'],
    }),
    /** Ручной статус элемента (КR7, только проверяющий ПТО площадки): `accepted` —
     *  элемент принят целиком (статус ручной, программа его не меняет); `auto` —
     *  вернуть статус программе (посчитает по заявкам и фактам). */
    setFieldElementStatus: b.mutation<
      { ok?: boolean; status?: string; element?: FieldElement },
      { elementId: string; status: 'accepted' | 'auto' }
    >({
      query: ({ elementId, status }) => ({
        url: `/agents/field/elements/${elementId}/status`,
        method: 'POST',
        body: { status },
      }),
      invalidatesTags: ['Field'],
    }),
    /** Заявки на освидетельствование площадки (executive_act_id → мост к АОСР). */
    getFieldInspectionRequests: b.query<{ requests: FieldInspectionRequest[] }, string>({
      query: (id) => `/agents/field/sites/${id}/inspection-requests`,
      providesTags: ['Field'],
    }),
    /** Переход заявки ПТО: sent → scheduled → passed | failed (409 — недопустимо). */
    setFieldInspectionRequestStatus: b.mutation<
      { request: FieldInspectionRequest },
      { id: string; status: string }
    >({
      query: ({ id, status }) => ({
        url: `/agents/field/inspection-requests/${id}/status`,
        method: 'POST',
        body: { status },
      }),
      invalidatesTags: ['Field'],
    }),
    /** HITL-решение по заявке (П2): accepted | rejected | disputed. */
    setFieldInspectionRequestDecision: b.mutation<
      { ok: boolean; decision: string },
      { id: string; decision: string }
    >({
      query: ({ id, decision }) => ({
        url: `/agents/field/inspection-requests/${id}/decision`,
        method: 'POST',
        body: { decision },
      }),
      invalidatesTags: ['Field'],
    }),
    /** Собрать АОСР по пройденной заявке (мост field→pto). Идемпотентно: акт уже
     *  собран — сервер вернёт `{act_id, already: true}`. Акт появляется в реестре
     *  актов ИД ОСНОВА.Инженер, сквозная заявка — в реестре заявок. */
    generateFieldInspectionAct: b.mutation<
      {
        act?: PtoExecutiveAct;
        request?: FieldInspectionRequest;
        act_id?: string;
        already?: boolean;
        /** Трассировка сборки; `evidence_notes` — пометки о доказательной базе,
         *  подобранной по периоду заявки (в текст акта не идут, КR9). */
        evidence?: { evidence_notes?: string[] | null } | null;
      },
      { id: string; act_no?: string; act_date?: string; object_name?: string }
    >({
      query: ({ id, ...body }) => ({
        url: `/agents/field/inspection-requests/${id}/generate-act`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Field', 'PtoExecAct', 'Requests'],
    }),
    /** Сменные рапорты площадки (шапки; работы — отдельным GET по рапорту). */
    getFieldDailyReports: b.query<
      { reports: FieldDailyReport[]; total: number; limit: number; offset: number },
      { siteId: string; author_id?: string; limit?: number; offset?: number }
    >({
      query: ({ siteId, ...params }) => ({
        url: `/agents/field/sites/${siteId}/daily-reports`,
        params,
      }),
      providesTags: ['Field'],
    }),
    /** Полный реестр метаданных для поиска пропущенной погоды ОЖР; без тихого усечения первой страницей. */
    getAllFieldDailyReports: b.query<{ reports: FieldDailyReport[] }, string>({
      async queryFn(siteId, api, _extra, baseQuery) {
        const reports: FieldDailyReport[] = [];
        for (let offset = 0; ; offset += 200) {
          if (api.signal.aborted)
            return { error: { status: 'CUSTOM_ERROR', error: 'Загрузка рапортов отменена' } };
          const result = await baseQuery({
            url: `/agents/field/sites/${siteId}/daily-reports`,
            params: { limit: 200, offset },
          });
          if (result.error) return { error: result.error };
          const page = result.data as { reports: FieldDailyReport[]; total: number };
          reports.push(...page.reports);
          if (page.reports.length < 200 || reports.length >= page.total)
            return { data: { reports } };
        }
      },
      providesTags: ['Field'],
    }),
    createFieldDailyReport: b.mutation<
      { report: FieldDailyReport; existing?: boolean },
      {
        siteId: string;
        shift_date: string;
        shift_type: string;
        note?: string;
        weather?: string;
        client_uuid?: string;
      }
    >({
      query: ({ siteId, ...body }) => ({
        url: `/agents/field/sites/${siteId}/daily-reports`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Field'],
    }),
    addFieldReportWork: b.mutation<
      { work: FieldReportWork },
      {
        reportId: string;
        name: string;
        element_id: string;
        unit: string;
        qty_fact: string;
        comment?: string;
        client_uuid: string;
      }
    >({
      query: ({ reportId, ...body }) => ({
        url: `/agents/field/daily-reports/${reportId}/works`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Field'],
    }),
    deleteFieldReportWork: b.mutation<{ ok: boolean }, { reportId: string; workId: string }>({
      query: ({ reportId, workId }) => ({
        url: `/agents/field/daily-reports/${reportId}/works/${workId}/delete`,
        method: 'POST',
      }),
      invalidatesTags: ['Field'],
    }),
    uploadFieldReportPhoto: b.mutation<{ photo: FieldPhoto }, { siteId: string; body: FormData }>({
      query: ({ siteId, body }) => ({
        url: `/agents/field/sites/${siteId}/photos`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Field'],
    }),
    /** Аномалии площадки (детектор L-6 + self-criticism). */
    getFieldAnomalies: b.query<{ anomalies: FieldAnomaly[] }, string>({
      query: (id) => `/agents/field/sites/${id}/anomalies`,
      providesTags: ['Field'],
    }),
    /** Карточка рапорта: шапка + строки работ с остатком (план − факт, сервер) и
     *  фактом объёма строки с решением ПТО (`works[].fact`, КR4). `return_allowed`
     *  — проверяющий может вернуть принятый рапорт (есть отклонения ПТО после
     *  последней сдачи); поля нет — старый сервер. */
    getFieldDailyReport: b.query<
      {
        report: FieldDailyReport;
        works: FieldReportWork[];
        resources: FieldReportResource[];
        usage: FieldReportUsage[];
        notes: FieldReportNote[];
        return_allowed?: boolean;
      },
      string
    >({
      query: (id) => `/agents/field/daily-reports/${id}`,
      providesTags: ['Field'],
    }),
    /** Приёмка рапорта ПТО: accepted | returned (+комментарий возврата, СР-5).
     *  Повтор текущего статуса тем, кто вправе, — 200 `{report, unchanged: true}`
     *  без изменений (рапорт уже в этом статусе). */
    setFieldReportStatus: b.mutation<
      { report: FieldDailyReport; unchanged?: boolean },
      { id: string; status: string; comment?: string }
    >({
      query: ({ id, status, comment }) => ({
        url: `/agents/field/daily-reports/${id}/status`,
        method: 'POST',
        body: { status, comment },
      }),
      invalidatesTags: ['Field'],
    }),
    /** Погода, условия смены (графа 3 ОЖР, КW1/КR8): проверяющий ПТО площадки — в
     *  любом статусе рапорта, писатель площадки — пока рапорт у прораба (черновик
     *  или возврат; иначе 409). Пустая строка — очистить. Сервер переносит погоду
     *  в строки ОЖР рапорта — их кэш тоже сбрасываем. */
    setFieldReportWeather: b.mutation<
      { report: FieldDailyReport },
      { id: string; weather: string }
    >({
      query: ({ id, weather }) => ({
        url: `/agents/field/daily-reports/${id}/weather`,
        method: 'POST',
        body: { weather },
      }),
      invalidatesTags: ['Field', 'PtoOzhr'],
    }),
    /** §8.1 Этап 2b: (пере)отправить прорабу зачитку распознанных чисел объёма в
     *  VK-бот (кнопки «Верно»/«Исправить»). Заверение до отправки не сбрасывается:
     *  несостоявшаяся отправка (нет VK ID у автора, сбой VK) верное «Верно» не
     *  теряет. VK ID задаёт администратор в разделе «Пользователи». */
    resendFieldVoiceReadback: b.mutation<
      // 200 — отправлено; 409/422 — не отправлено, причина в reason/error.message.
      { ok: boolean; sent?: boolean; reason?: string; vk_enabled: boolean },
      string
    >({
      query: (id) => ({
        url: `/agents/field/daily-reports/${id}/voice-readback`,
        method: 'POST',
      }),
      invalidatesTags: ['Field'],
    }),
    /** §8.1 Реестр фактов объёма по проекту (инструмент ПТО): все статусы. */
    getProjectVolumeFacts: b.query<{ facts: VolumeFact[] }, string>({
      query: (projectId) => `/agents/field/projects/${projectId}/volume-facts`,
      providesTags: ['Field'],
    }),
    /** Факты объёма по площадке (прораб — свои; ПТО — сквозным доступом). */
    getSiteVolumeFacts: b.query<{ facts: VolumeFact[] }, string>({
      query: (siteId) => `/agents/field/sites/${siteId}/volume-facts`,
      providesTags: ['Field'],
    }),
    /** Карточка факта объёма: сам факт + журнал переходов (§8.1). */
    getVolumeFact: b.query<{ fact: VolumeFact; events: VolumeFactEvent[] }, string>({
      query: (id) => `/agents/field/volume-facts/${id}`,
      providesTags: ['Field'],
    }),
    /** Сверка факта инженером ПТО: accept | correct (corrected_qty) | reject — по
     *  заявленному факту и по эскалации (прораб не согласен с правкой или не
     *  подтвердил её в срок, КW2). Принятый факт становится строкой раздела 3 ОЖР
     *  — её кэш тоже сбрасываем.
     *  `warning` — решение принято, но объём выходит за РД (один или вместе с
     *  нерешёнными заявками того же вида): показать инженеру. */
    verifyVolumeFact: b.mutation<
      { fact: VolumeFact; warning?: string | null },
      { id: string; action: string; corrected_qty?: number; note?: string }
    >({
      query: ({ id, ...body }) => ({
        url: `/agents/field/volume-facts/${id}/verify`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Field', 'PtoOzhr'],
    }),
    /** Подтверждение прорабом правки инженера (снимает «красную лампочку»);
     *  факт становится принятым — и строкой ОЖР. `warning` — превышение РД.
     *  Повтор по уже принятому этим же прорабом — 200 `{fact, unchanged: true}`. */
    confirmVolumeFact: b.mutation<
      { fact: VolumeFact; warning?: string | null; unchanged?: boolean },
      string
    >({
      query: (id) => ({
        url: `/agents/field/volume-facts/${id}/confirm`,
        method: 'POST',
      }),
      invalidatesTags: ['Field', 'PtoOzhr'],
    }),
    /** «Не согласен» прораба с правкой ПТО (КW2): только прораб/начальник участка
     *  площадки и только пока правка ждёт его (иначе 409); комментарий обязателен
     *  (3–1000 символов). Факт уходит на эскалацию — решение принимает ПТО. */
    disputeVolumeFact: b.mutation<{ fact: VolumeFact }, { id: string; comment: string }>({
      query: ({ id, comment }) => ({
        url: `/agents/field/volume-facts/${id}/dispute`,
        method: 'POST',
        body: { comment },
      }),
      invalidatesTags: ['Field'],
    }),
    /** Привязать факт к конструктиву площадки (только проверяющий ПТО; статусы
     *  declared | corrected_pending_foreman). Сервер пересчитывает сверку с РД по
     *  позиции элемента и снимает пометку «подобрано автоматически» — привязка к
     *  тому же элементу и есть подтверждение автопривязки. */
    bindVolumeFact: b.mutation<{ fact: VolumeFact }, { id: string; element_id: string }>({
      query: ({ id, element_id }) => ({
        url: `/agents/field/volume-facts/${id}/bind`,
        method: 'POST',
        body: { element_id },
      }),
      invalidatesTags: ['Field'],
    }),
    /** Фото площадки (метаданные; байты — отдельным GET /photos/{id}/data). */
    getFieldPhotos: b.query<
      { photos: FieldPhoto[]; total: number; limit: number; offset: number },
      {
        siteId: string;
        category?: string;
        report_id?: string;
        author_id?: string;
        inspection_request_id?: string;
        limit?: number;
        offset?: number;
      }
    >({
      query: ({ siteId, ...params }) => ({ url: `/agents/field/sites/${siteId}/photos`, params }),
      providesTags: ['Field'],
    }),
    /** Байты фото как Blob (RBAC через Authorization; лениво — для превью). */
    getFieldPhotoBlob: b.query<Blob, string>({
      query: (id) => ({
        url: `/agents/field/photos/${id}/data`,
        cache: 'no-store',
        responseHandler: (response) => response.blob(),
      }),
    }),
    /** XLSX-реестр рапортов площадки (СР-5; листы: рапорты/работы/техника/
     *  персонал/материалы/проблемы). Лениво — по клику. */
    exportFieldReportsXlsx: b.query<Blob, string>({
      query: (siteId) => ({
        url: `/agents/field/sites/${siteId}/export/reports.xlsx`,
        cache: 'no-store',
        responseHandler: (response) => response.blob(),
      }),
    }),
    /** XLSX-сводка объёмов план/факт/остаток (числа считает сервер, ПР-8). */
    exportFieldVolumesXlsx: b.query<Blob, string>({
      query: (siteId) => ({
        url: `/agents/field/sites/${siteId}/export/volumes.xlsx`,
        cache: 'no-store',
        responseHandler: (response) => response.blob(),
      }),
    }),
    /** PDF-фотоотчёт с водяными знаками (ПР-7: оригиналы не модифицируются).
     *  Категория опциональна; сервер ограничивает 150 фото на отчёт. */
    exportFieldPhotoReportPdf: b.query<Blob, { siteId: string; category?: string }>({
      query: ({ siteId, category }) => ({
        url: `/agents/field/sites/${siteId}/export/photo-report.pdf${
          category ? `?category=${encodeURIComponent(category)}` : ''
        }`,
        cache: 'no-store',
        responseHandler: (response) => response.blob(),
      }),
    }),
    /** HITL-решение по аномалии: acknowledged | rejected. */
    setFieldAnomalyDecision: b.mutation<
      { anomaly: FieldAnomaly },
      { siteId: string; anomalyId: string; decision: string }
    >({
      query: ({ siteId, anomalyId, decision }) => ({
        url: `/agents/field/sites/${siteId}/anomalies/${anomalyId}/decision`,
        method: 'POST',
        body: { decision },
      }),
      invalidatesTags: ['Field'],
    }),
    /** Задачи/предписания площадки (МП-7). */
    getFieldTasks: b.query<{ tasks: FieldTask[] }, string>({
      query: (siteId) => `/agents/field/sites/${siteId}/tasks`,
      providesTags: ['Field'],
    }),
    /** Создать задачу/предписание (ПТО/стройконтроль, глобальный доступ). */
    createFieldTask: b.mutation<
      { task: FieldTask },
      {
        siteId: string;
        source: string;
        text: string;
        dueDate?: string;
        requiresPhoto?: boolean;
      }
    >({
      query: ({ siteId, source, text, dueDate, requiresPhoto }) => ({
        url: `/agents/field/sites/${siteId}/tasks`,
        method: 'POST',
        body: {
          source,
          text,
          due_date: dueDate,
          requires_photo: requiresPhoto ?? false,
        },
      }),
      invalidatesTags: ['Field'],
    }),
    /** Статусный переход задачи (in_progress/done/declined+причина). */
    setFieldTaskStatus: b.mutation<
      { task: FieldTask },
      { taskId: string; status: string; declineReason?: string }
    >({
      query: ({ taskId, status, declineReason }) => ({
        url: `/agents/field/tasks/${taskId}/status`,
        method: 'POST',
        body: { status, decline_reason: declineReason },
      }),
      invalidatesTags: ['Field'],
    }),
    /** Канал VK Teams площадки для дайджестов L-7 (null/пусто — отключить). */
    setFieldSiteVkChat: b.mutation<
      { site: FieldSite },
      { siteId: string; vkChatId: string | null }
    >({
      query: ({ siteId, vkChatId }) => ({
        url: `/agents/field/sites/${siteId}/vk-chat`,
        method: 'POST',
        body: { vk_chat_id: vkChatId },
      }),
      invalidatesTags: ['Field'],
    }),
    /** Геозона площадки (МП-3.1): все три поля — задать круг, все три null — сбросить. */
    setFieldSiteGeofence: b.mutation<
      { site: FieldSite },
      {
        siteId: string;
        centerLat: number | null;
        centerLon: number | null;
        radiusM: number | null;
      }
    >({
      query: ({ siteId, centerLat, centerLon, radiusM }) => ({
        url: `/agents/field/sites/${siteId}/geofence`,
        method: 'POST',
        body: { center_lat: centerLat, center_lon: centerLon, radius_m: radiusM },
      }),
      invalidatesTags: ['Field'],
    }),

    // ── Электронные заявки между функциями (requests-core, ADR-0016) ──
    /** Реестр заявок: box=outgoing (мои) | incoming (входящие target-функции). */
    getRequests: b.query<
      { requests: FunctionRequest[] },
      { box: string; function?: string; status?: string; type?: string }
    >({
      query: (arg) => ({
        url: '/requests',
        params: {
          box: arg.box,
          ...(arg.function ? { function: arg.function } : {}),
          ...(arg.status ? { status: arg.status } : {}),
          ...(arg.type ? { type: arg.type } : {}),
        },
      }),
      providesTags: ['Requests'],
    }),
    /** Сводный реестр Диспетчера (Phase 4): все заявки + фильтры. RBAC — глобальный
     *  доступ ПТО/Прораб; иначе 403 (обрабатываем на странице как «нет доступа»). */
    getRequestsRegistry: b.query<
      { requests: FunctionRequest[] },
      { function?: string; type?: string; status?: string; project_id?: string; overdue?: boolean }
    >({
      query: (arg) => ({
        url: '/requests/registry',
        params: {
          ...(arg.function ? { function: arg.function } : {}),
          ...(arg.type ? { type: arg.type } : {}),
          ...(arg.status ? { status: arg.status } : {}),
          ...(arg.project_id ? { project_id: arg.project_id } : {}),
          ...(arg.overdue ? { overdue: true } : {}),
        },
      }),
      providesTags: ['Requests'],
    }),
    /** Карточка заявки + история переходов + ответ исполнителя (для заявок,
     *  где результат — ответ текстом и файлом: `mto_supply`). */
    getRequest: b.query<
      {
        request: FunctionRequest;
        transitions: RequestTransition[];
        response: RequestResponse | null;
      },
      string
    >({
      query: (id) => `/requests/${id}`,
      providesTags: ['Requests'],
    }),
    /** Ответ исполнителя: текст + необязательный файл; `complete` — сразу
     *  выполнить заявку этим ответом (`in_progress → completed`). */
    respondToRequest: b.mutation<
      { request: FunctionRequest; response: RequestResponse },
      { id: string; text: string; file?: File | null; complete?: boolean }
    >({
      query: ({ id, text, file, complete }) => {
        const form = new FormData();
        form.append('text', text);
        if (complete) form.append('complete', 'true');
        if (file) form.append('file', file, file.name);
        return { url: `/requests/${id}/response`, method: 'POST', body: form };
      },
      invalidatesTags: ['Requests'],
    }),
    /** Файл ответа исполнителя — по клику, без кэша. */
    downloadRequestResponseFile: b.query<Blob, string>({
      query: (id) => ({
        url: `/requests/${id}/response/file`,
        cache: 'no-store',
        responseHandler: fileResponse,
      }),
    }),
    /** Связанные заявки (§1 linked_requests). */
    getRequestLinks: b.query<{ links: FunctionRequest[] }, string>({
      query: (id) => `/requests/${id}/links`,
      providesTags: ['Requests'],
    }),
    /** Создать/подать заявку (§8). */
    createRequest: b.mutation<
      { request: FunctionRequest },
      {
        request_type: string;
        source_function: string;
        title: string;
        priority?: string;
        project_id?: string;
        site_id?: string;
        element_id?: string;
        due_date?: string;
        payload?: unknown;
        /** Сразу подать (`draft → submitted`): заявка без кнопки «Подать»
         *  (например, «Нет схемы — запросить у геодезии» из ПТО). */
        submit?: boolean;
      }
    >({
      query: (body) => ({ url: '/requests', method: 'POST', body }),
      invalidatesTags: ['Requests'],
    }),
    /** Переход статуса по действию (submit/accept/reject/progress/complete/close/cancel). */
    transitionRequest: b.mutation<
      { request: FunctionRequest },
      { id: string; action: string; reason?: string }
    >({
      query: ({ id, ...body }) => ({
        url: `/requests/${id}/transition`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Requests'],
    }),
    /** Прикрепить результат-артефакт (§5). */
    attachRequestResult: b.mutation<
      { request: FunctionRequest },
      { id: string; result_ref: string; result_kind?: string }
    >({
      query: ({ id, ...body }) => ({
        url: `/requests/${id}/result`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Requests'],
    }),
    /** Кандидаты в связь — заявки того же элемента, ещё не связанные (§1). */
    getRequestLinkCandidates: b.query<{ candidates: FunctionRequest[] }, string>({
      query: (id) => `/requests/${id}/link-candidates`,
      providesTags: ['Requests'],
    }),
    /** Связать текущую заявку с другой (§1, двунаправленно). */
    linkRequest: b.mutation<{ links: FunctionRequest[] }, { id: string; other_id: string }>({
      query: ({ id, other_id }) => ({
        url: `/requests/${id}/links`,
        method: 'POST',
        body: { other_id },
      }),
      invalidatesTags: ['Requests'],
    }),
    /** Снять связь текущей заявки с другой (§1, двунаправленно). */
    unlinkRequest: b.mutation<{ links: FunctionRequest[] }, { id: string; other_id: string }>({
      query: ({ id, other_id }) => ({
        url: `/requests/${id}/links/${other_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Requests'],
    }),
    /** Конвертировать заявку в юрслужбу в профильную сущность ОСНОВА.Юрист. */
    convertRequestToStatut: b.mutation<
      {
        request: FunctionRequest;
        entity_kind: string;
        entity_id: string;
        entity_url: string;
      },
      string
    >({
      query: (id) => ({
        url: `/requests/${id}/convert-to-statut`,
        method: 'POST',
      }),
      invalidatesTags: ['Requests', 'Contract', 'Claim', 'Correspondence', 'Counterparty'],
    }),
    // ── Приёмка материалов на площадке (ТТН и паспорта качества, 0135) ──
    getFieldMaterialReceipts: b.query<{ receipts: FieldMaterialReceipt[] }, string>({
      query: (siteId) => `/agents/field/sites/${siteId}/material-receipts`,
      providesTags: ['Field'],
    }),
    uploadFieldMaterialReceipt: b.mutation<
      { receipt: FieldMaterialReceipt; queued: boolean },
      { siteId: string; file: File; elementId?: string; reportId?: string }
    >({
      query: ({ siteId, file, elementId, reportId }) => {
        const form = new FormData();
        form.append('file', file, file.name);
        if (elementId) form.append('element_id', elementId);
        if (reportId) form.append('report_id', reportId);
        return {
          url: `/agents/field/sites/${siteId}/material-receipts`,
          method: 'POST',
          body: form,
        };
      },
      invalidatesTags: ['Field'],
    }),
    attachFieldReceiptPassport: b.mutation<
      { receipt: FieldMaterialReceipt },
      { id: string; passport_id?: string | null; lot_id?: string | null }
    >({
      query: ({ id, ...body }) => ({
        url: `/agents/field/material-receipts/${id}/passport`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Field'],
    }),
    confirmFieldMaterialReceipt: b.mutation<
      { receipt: FieldMaterialReceipt; unchanged?: boolean },
      string
    >({
      query: (id) => ({
        url: `/agents/field/material-receipts/${id}/confirm`,
        method: 'POST',
      }),
      invalidatesTags: ['Field'],
    }),
    deleteFieldMaterialReceipt: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({
        url: `/agents/field/material-receipts/${id}/delete`,
        method: 'POST',
      }),
      invalidatesTags: ['Field'],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetFieldInfoQuery,
  useGetFieldSitesQuery,
  useGetFieldSiteQuery,
  useCreateFieldSiteMutation,
  useGetFieldSiteMembersQuery,
  useGetFieldAssignableUsersQuery,
  useAddFieldSiteMemberMutation,
  useGetFieldElementsQuery,
  useCreateFieldElementsFromRdMutation,
  useGetFieldSiteRdPositionsQuery,
  useSetFieldElementPositionMutation,
  useSetFieldElementStatusMutation,
  useGetFieldInspectionRequestsQuery,
  useSetFieldInspectionRequestStatusMutation,
  useSetFieldInspectionRequestDecisionMutation,
  useGenerateFieldInspectionActMutation,
  useGetFieldDailyReportsQuery,
  useGetAllFieldDailyReportsQuery,
  useCreateFieldDailyReportMutation,
  useAddFieldReportWorkMutation,
  useDeleteFieldReportWorkMutation,
  useUploadFieldReportPhotoMutation,
  useGetFieldTitulsQuery,
  useCreateFieldTitulMutation,
  useUpdateFieldTitulMutation,
  useSetFieldTitulDocumentsMutation,
  useSetFieldSiteTitulMutation,
  useGetFieldAnomaliesQuery,
  useGetFieldDailyReportQuery,
  useSetFieldReportStatusMutation,
  useSetFieldReportWeatherMutation,
  useResendFieldVoiceReadbackMutation,
  useGetProjectVolumeFactsQuery,
  useGetSiteVolumeFactsQuery,
  useGetVolumeFactQuery,
  useVerifyVolumeFactMutation,
  useConfirmVolumeFactMutation,
  useDisputeVolumeFactMutation,
  useBindVolumeFactMutation,
  useGetFieldPhotosQuery,
  useLazyGetFieldPhotoBlobQuery,
  useSetFieldAnomalyDecisionMutation,
  useSetFieldSiteVkChatMutation,
  useSetFieldSiteGeofenceMutation,
  useLazyExportFieldReportsXlsxQuery,
  useLazyExportFieldVolumesXlsxQuery,
  useLazyExportFieldPhotoReportPdfQuery,
  useGetFieldTasksQuery,
  useCreateFieldTaskMutation,
  useSetFieldTaskStatusMutation,
  useGetRequestsQuery,
  useGetRequestsRegistryQuery,
  useGetRequestQuery,
  useGetRequestLinksQuery,
  useGetRequestLinkCandidatesQuery,
  useCreateRequestMutation,
  useTransitionRequestMutation,
  useAttachRequestResultMutation,
  useRespondToRequestMutation,
  useLazyDownloadRequestResponseFileQuery,
  useLinkRequestMutation,
  useUnlinkRequestMutation,
  useConvertRequestToStatutMutation,
  useGetFieldMaterialReceiptsQuery,
  useUploadFieldMaterialReceiptMutation,
  useAttachFieldReceiptPassportMutation,
  useConfirmFieldMaterialReceiptMutation,
  useDeleteFieldMaterialReceiptMutation,
} = fieldApi;
